import React, { useState, useEffect, useCallback, useMemo, useRef, useContext, createContext } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import moment from 'moment';
import Input from '@material-ui/core/Input';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import CircularProgress from '@material-ui/core/CircularProgress';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import AssessmentIcon from '@material-ui/icons/Assessment';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import List from '~/containers/List';
import Form from '~/containers/Form';
import Paper from '~/components/Paper';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import InputControl from '~/components/Input/InputControl';
import InputDate from '~/components/Input/InputDate';
import InputSelect from '~/components/Input/InputSelect';
import InputSwitch from '~/components/Input/InputSwitch';
import Request from '~/providers/Request';
import AppointmentConfirmationSettingsRecord from '~/records/AppointmentConfirmationSettingsRecord';
import AppointmentConfirmationContext from './context';
import useStyles from './styles';

const PATH = '/admin/appointment_confirmations';
const ALL_STATUSES = 'all';
const STATUSES = ['pending', 'sent', 'failed', 'skipped'];
const RESULT_ROWS = ['sent', 'delivered', 'undelivered', 'waiting', 'failed', 'skipped'];

const periodParams = (from, until) => {
  const params = {};
  if (from) params.created_from = moment(from).startOf('day').format();
  if (until) params.created_until = moment(until).endOf('day').format();
  return params;
};

// Mesmo formato que o filtro genérico das listas manda para a API.
const buildFilters = (from, until, status) => {
  const filters = [];
  const start = from ? moment(from).startOf('day').subtract(1, 'second').format() : null;
  const end = until ? moment(until).endOf('day').format() : null;
  if (start && end) filters.push({ column: 'created_at', operation: 'between', value: `${start}|${end}` });
  else if (start) filters.push({ column: 'created_at', operation: 'greaterThan', value: start });
  else if (end) filters.push({ column: 'created_at', operation: 'lessThan', value: end });
  if (status !== ALL_STATUSES) filters.push({ column: 'status', operation: 'equal', value: status });
  return filters.length ? JSON.stringify({ groups: [{ condition: 'and', filters }] }) : '';
};

const PanelContext = createContext({});

// O card da lista é montado pelo container da tabela; definido fora da página para não remontar a tabela a cada filtro.
function ConfirmationsPanel({ children }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const tr = (key) => t(`appointmentConfirmation.extras.${key}`);
  const { status, setStatus } = useContext(PanelContext);
  const statusOptions = [
    { id: ALL_STATUSES, name: tr('statusAll') },
    ...STATUSES.map((value) => ({ id: value, name: tr(`statuses.${value}`) })),
  ];

  return (
    <Paper Icon={WhatsAppIcon} iconColor="green" title={tr('listTitle')}>
      <div className={classes.filterRow}>
        <GridContainer>
          <GridItem xs={12} md={4}>
            <InputControl
              name="status"
              value={status}
              label={tr('statusFilter')}
              inputComponent={InputSelect}
              onChange={({ status: selected }) => setStatus(selected || ALL_STATUSES)}
              InputProps={{ options: statusOptions, outValueType: 'string' }}
            />
          </GridItem>
        </GridContainer>
      </div>
      {children}
    </Paper>
  );
}

const displayStatus = (record) => {
  if (record.status !== 'sent') return record.status;
  if (record.delivered_at) return 'delivered';
  return record.delivery_error ? 'undelivered' : 'sent';
};

function AppointmentConfirmations() {
  const classes = useStyles();
  const { t } = useTranslation();
  const tr = (key, options) => t(`appointmentConfirmation.extras.${key}`, options);
  const trAttr = (attr) => t(`appointmentConfirmation.attributes.${attr}`);
  const listEl = useRef(null);

  const [record, setRecord] = useState(new AppointmentConfirmationSettingsRecord());
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdUntil, setCreatedUntil] = useState('');
  const [status, setStatus] = useState(ALL_STATUSES);

  const filters = useMemo(() => buildFilters(createdFrom, createdUntil, status), [createdFrom, createdUntil, status]);
  const formatDate = (value) => (value ? moment(value).format('DD/MM/YYYY HH:mm') : '');
  const enabled = Boolean(record.get('appointment_confirmation_enabled'));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Request.get(`${PATH}/settings`, {});
      setRecord((current) => current.updateAttributes(response.data));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSummary = useCallback(async (from, until) => {
    setSummaryLoading(true);
    try {
      const response = await Request.get(`${PATH}/summary`, periodParams(from, until), { normalizeJSON: false });
      setSummary(response.data);
    } catch (e) {
      console.error(e);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadSummary(createdFrom, createdUntil);
  }, [loadSummary, createdFrom, createdUntil]);

  const handleChange = (attrs) => setRecord(record.updateAttributes(attrs));

  async function onSubmit() {
    try {
      const response = await Request.put(`${PATH}/settings`, record.toParams(), {}, { controller: 'appointmentConfirmation' });
      setRecord(record.updateAttributes(response.data));
      toast.success(tr('saved'));
    } catch (e) {
      console.error(e);
    }
  }

  const total = summary ? RESULT_ROWS.reduce((sum, key) => (['delivered', 'undelivered'].includes(key) ? sum : sum + Number(summary[key] || 0)), 0) : 0;
  const reasons = (summary && summary.skipped_reasons) || [];

  return (
    <PanelContext.Provider value={{ status, setStatus }}>
      <Form onSubmit={onSubmit} isPersisted showCancel={false}>
        <Paper Icon={EventAvailableIcon} iconColor="green" title={tr('settingsTitle')} loading={loading}>
          <div className={classes.section}>
            <GridContainer>
              <GridItem xs={12} md={4}>
                <InputControl
                  name="appointment_confirmation_template"
                  value={record.get('appointment_confirmation_template') || ''}
                  label={trAttr('appointment_confirmation_template')}
                  required={enabled}
                  inputComponent={Input}
                  onChange={handleChange}
                />
              </GridItem>
              <GridItem xs={12} md={4}>
                <InputControl
                  name="appointment_confirmation_contact"
                  value={record.get('appointment_confirmation_contact') || ''}
                  label={trAttr('appointment_confirmation_contact')}
                  required={enabled}
                  inputComponent={Input}
                  onChange={handleChange}
                />
                <Typography className={classes.help}>{tr('contactHelp')}</Typography>
              </GridItem>
              <GridItem xs={12} md={4}>
                <div className={classes.switchCell}>
                  <InputSwitch
                    value={enabled}
                    label={trAttr('appointment_confirmation_enabled')}
                    onChange={(checked) => handleChange({ appointment_confirmation_enabled: checked })}
                  />
                </div>
              </GridItem>
            </GridContainer>
          </div>
        </Paper>
      </Form>

      <Paper Icon={AssessmentIcon} iconColor="purple" title={tr('resultsTitle')} loading={summaryLoading && !summary}>
        <div className={classes.section}>
          <GridContainer>
            <GridItem xs={12} md={3}>
              <InputControl
                name="created_from"
                value={createdFrom}
                label={tr('createdFrom')}
                inputComponent={InputDate}
                InputProps={{ type: 'date' }}
                onChange={(date) => setCreatedFrom(date || '')}
              />
            </GridItem>
            <GridItem xs={12} md={3}>
              <InputControl
                name="created_until"
                value={createdUntil}
                label={tr('createdUntil')}
                inputComponent={InputDate}
                InputProps={{ type: 'date' }}
                onChange={(date) => setCreatedUntil(date || '')}
              />
            </GridItem>
          </GridContainer>
          {summaryLoading && summary && <CircularProgress size={18} className={classes.inlineProgress} />}
          {summary && !total && <Typography className={classes.emptyState}>{tr('empty')}</Typography>}
          {summary && Boolean(total) && (
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.stageCell}>{tr('resultStage')}</TableCell>
                  <TableCell align="right">{tr('resultTotal')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {RESULT_ROWS.map((key) => (
                  <TableRow key={key}>
                    <TableCell>{tr(`results.${key}`)}</TableCell>
                    <TableCell align="right">{Number(summary[key] || 0).toLocaleString('pt-BR')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {Boolean(reasons.length) && (
            <>
              <Typography className={classes.subtitle}>{tr('reasonsTitle')}</Typography>
              <Table size="small" className={classes.table}>
                <TableBody>
                  {reasons.map(({ reason, total: count }) => (
                    <TableRow key={reason}>
                      <TableCell className={classes.stageCell}>{tr(`reasons.${reason}`, { defaultValue: reason })}</TableCell>
                      <TableCell align="right">{Number(count).toLocaleString('pt-BR')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </div>
      </Paper>

      <List
        listEl={listEl}
        path={PATH}
        actions={[]}
        options={{ toolbar: false }}
        fixedQuery={filters ? { filters } : {}}
        fetchFlag={filters}
        paperProps={{ Icon: WhatsAppIcon, iconColor: 'green', title: tr('listTitle') }}
        components={{ Container: ConfirmationsPanel }}
        columns={[
          { title: trAttr('patient_name'), field: 'patient_name', type: 'string' },
          { title: trAttr('phone'), field: 'phone', type: 'string' },
          { title: trAttr('scheduled_at'), field: 'scheduled_at', type: 'string' },
          { title: trAttr('doctor_name'), field: 'doctor_name', type: 'string' },
          { title: trAttr('specialty_name'), field: 'specialty_name', type: 'string' },
          { title: trAttr('status'), field: 'status', type: 'string' },
          { title: trAttr('skip_reason'), field: 'skip_reason', type: 'string' },
          { title: trAttr('created_at'), field: 'created_at', type: 'string' },
          { title: trAttr('sent_at'), field: 'sent_at', type: 'string' },
          { title: trAttr('delivered_at'), field: 'delivered_at', type: 'string' },
        ]}
        context={AppointmentConfirmationContext}
        recordMap={(row) => ({
          ...row,
          status: tr(`statuses.${displayStatus(row)}`, { defaultValue: row.status }),
          skip_reason: row.skip_reason ? tr(`reasons.${row.skip_reason}`, { defaultValue: row.skip_reason }) : (row.error || row.delivery_error || ''),
          scheduled_at: formatDate(row.scheduled_at),
          created_at: formatDate(row.created_at),
          sent_at: formatDate(row.sent_at),
          delivered_at: formatDate(row.delivered_at),
        })}
      />
    </PanelContext.Provider>
  );
}

export default React.memo(AppointmentConfirmations);
